import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createAuthService } from "../firebase/auth";
import { isAdminEmail } from "../firebase/roles";
import { readDocument, updateDocument } from "../firebase/db";

export type UserRole = "public" | "user" | "admin";

interface AuthContextValue {
  role: UserRole;
  isAuthenticated: boolean;
  userId: string | null;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
};

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const auth = useMemo(() => createAuthService(), []);
  const navigate = useNavigate();
  const [role, setRole] = useState<UserRole>("public");
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const unsub = auth.onAuthChange(async (user) => {
      if (user) {
        // Check if user is active before setting authenticated state
        try {
          const userDoc = await readDocument<{ isActive?: boolean; isVerified?: boolean }>("user", user.uid, false);
          if (userDoc && userDoc.isActive === false) {
            // User is inactive, sign them out
            await auth.signOut();
            setIsAuthenticated(false);
            setUserId(null);
            setRole("public");
            return;
          }

          // Belt-and-suspenders sync: Firebase Auth is the source of truth
          // for email verification, but we mirror it on the Firestore user
          // doc for admin views and other UI. If the /verify-email page
          // couldn't write Firestore (e.g. user clicked the link in a
          // different browser without being signed in), catch up here on
          // their next login.
          if (
            user.emailVerified &&
            userDoc &&
            userDoc.isVerified !== true
          ) {
            try {
              await updateDocument("user", user.uid, { isVerified: true });
            } catch (syncErr) {
              console.warn("Could not sync isVerified to Firestore:", syncErr);
            }
          }
        } catch (error) {
          // If document doesn't exist or other error, allow authentication (for backward compatibility)
          console.warn("Could not check user active status:", error);
        }

        setIsAuthenticated(true);
        setUserId(user.uid);
        setRole(isAdminEmail(user.email) ? "admin" : "user");
      } else {
        setIsAuthenticated(false);
        setUserId(null);
        setRole("public");
      }
    });
    return () => unsub();
  }, [auth]);

  const value = useMemo<AuthContextValue>(() => ({
    role,
    isAuthenticated,
    userId,
    getIdToken: auth.getIdToken,
    logout: async () => {
      await auth.signOut();
      navigate("/login", { replace: true });
    },
  }), [role, isAuthenticated, userId, auth, navigate]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


